/**
 * A agregação — de linhas de uma tabela para pontos de um gráfico.
 *
 * **Puro**, como o avaliador de fórmula e pela mesma razão (decisão D-D do Discovery): somar,
 * agrupar e ordenar não têm "outro lado", e tudo o que se prova aqui não precisa de build nem
 * de navegador. O que o gráfico faz com os pontos é `recharts`; **quais** são os pontos é
 * decisão nossa, e é aqui.
 *
 * ## A decisão que governa o arquivo: o gráfico lê a VISÃO, não a tabela
 *
 * A T3.2 do card pede *"respeitando filtro e agrupamento da visão escolhida"*. Isso não é
 * detalhe de implementação — é o que torna o bloco útil: quem filtrou "Pago? = não" espera que
 * o gráfico ao lado mostre o que está na tela, não o razão inteiro.
 *
 * Então este módulo nunca recebe "a tabela". Ele recebe **as linhas que a visão já resolveu**,
 * e o chamador é quem lê `view.rows$` (filtrado e ordenado) em vez de `dataSource.rows$`.
 */

/** Uma linha, já reduzida ao que o gráfico precisa: valores por `propertyId`. */
export type LinhaDeDados = Record<string, unknown>;

export type Agregacao = 'soma' | 'media' | 'contagem' | 'minimo' | 'maximo' | 'nenhuma';

export const AGREGACOES: readonly Agregacao[] = [
  'soma', 'media', 'contagem', 'minimo', 'maximo', 'nenhuma',
] as const;

export type TipoDeGrafico =
  | 'barra'
  | 'barra-empilhada'
  | 'linha'
  | 'area'
  | 'pizza'
  | 'rosca'
  | 'combo';

export const TIPOS_DE_GRAFICO: readonly TipoDeGrafico[] = [
  'barra', 'barra-empilhada', 'linha', 'area', 'pizza', 'rosca', 'combo',
] as const;

/** Uma série é uma coluna de valores mais como ela aparece. */
export interface Serie {
  propertyId: string;
  rotulo: string;
  cor?: string;
  /** No combo: quem é barra e quem é linha. Ignorado nos outros tipos. */
  forma?: 'barra' | 'linha';
}

export interface Ponto {
  /** O rótulo do eixo X — sempre texto, porque é isso que o eixo desenha. */
  categoria: string;
  /** Um valor por série, na chave `propertyId`. `null` quando não há dado. */
  [serie: string]: string | number | null;
}

/**
 * `null` e `undefined` NÃO viram zero.
 *
 * Mesma decisão do `valores.ts` do avaliador, e pelo mesmo motivo: uma média que conta a
 * ausência como zero puxa o resultado para baixo sem dizer que puxou. `recharts` desenha
 * `null` como buraco na linha, que é a leitura honesta — *"não houve dado aqui"* em vez de
 * *"houve, e foi zero"*.
 *
 * ⚠️ Zero de verdade continua sendo zero. A distinção entre "não houve" e "houve e deu zero"
 * é justamente a que um fluxo de caixa precisa manter.
 */
function comoNumero(bruto: unknown): number | null {
  if (bruto === null || bruto === undefined || bruto === '') return null;
  if (typeof bruto === 'number') return Number.isFinite(bruto) ? bruto : null;
  if (typeof bruto === 'boolean') return bruto ? 1 : 0;
  if (typeof bruto === 'string') {
    const n = Number(bruto.replace(/\s/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Como uma categoria do eixo X é escrita. Vazio tem nome próprio — some seria pior. */
export function comoCategoria(bruto: unknown): string {
  if (bruto === null || bruto === undefined || bruto === '') return '(sem valor)';
  if (Array.isArray(bruto)) return bruto.length ? bruto.join(', ') : '(sem valor)';
  if (typeof bruto === 'boolean') return bruto ? 'sim' : 'não';
  return String(bruto);
}

function aplicar(agregacao: Agregacao, valores: (number | null)[]): number | null {
  const presentes = valores.filter((v): v is number => v !== null);
  if (agregacao === 'contagem') return valores.length;
  if (!presentes.length) return null;
  switch (agregacao) {
    case 'soma': return presentes.reduce((a, b) => a + b, 0);
    case 'media': return presentes.reduce((a, b) => a + b, 0) / presentes.length;
    case 'minimo': return Math.min(...presentes);
    case 'maximo': return Math.max(...presentes);
    case 'nenhuma':
      // Sem agregação, um grupo com mais de uma linha não tem resposta única. Devolver a
      // primeira seria escolher em silêncio; devolver a soma seria agregar sem dizer. O
      // desenho é: `nenhuma` só faz sentido quando cada categoria tem uma linha — e quando
      // não tem, o último valor VENCE, que é o comportamento previsível de uma série
      // temporal reescrita. Está dito aqui para não ser descoberto num gráfico torto.
      return presentes[presentes.length - 1];
    default: return null;
  }
}

export interface OpcoesDeAgregacao {
  eixoX: string;
  series: Serie[];
  agregacao: Agregacao;
  /**
   * Quantas categorias no máximo. As excedentes viram uma categoria `Outros`.
   *
   * ⚠️ Sem teto, um gráfico de barras sobre 420 lançamentos com fornecedor único por linha
   * desenha 420 barras de 1px — ilegível, e lento. Com teto e SEM `Outros`, o gráfico
   * mentiria por omissão: a soma das barras não bateria com o total. `Outros` é o que
   * mantém as duas coisas.
   */
  maximoDeCategorias?: number;
}

export const MAXIMO_DE_CATEGORIAS_PADRAO = 12;
export const CATEGORIA_OUTROS = 'Outros';

/**
 * As linhas viram pontos.
 *
 * A ordem das categorias é a de **primeira aparição** nas linhas recebidas — e como as linhas
 * já vêm ordenadas pela visão, ordenar o eixo X é ordenar a visão. É o que faz um gráfico de
 * evolução mensal sair na ordem dos meses sem ninguém configurar nada.
 */
export function agregar(
  linhas: LinhaDeDados[],
  opcoes: OpcoesDeAgregacao
): Ponto[] {
  const { eixoX, series, agregacao } = opcoes;
  const teto = opcoes.maximoDeCategorias ?? MAXIMO_DE_CATEGORIAS_PADRAO;

  if (!eixoX || !series.length) return [];

  const grupos = new Map<string, LinhaDeDados[]>();
  for (const linha of linhas) {
    const categoria = comoCategoria(linha[eixoX]);
    const atual = grupos.get(categoria);
    if (atual) atual.push(linha);
    else grupos.set(categoria, [linha]);
  }

  const pontoDe = (categoria: string, doGrupo: LinhaDeDados[]): Ponto => {
    const ponto: Ponto = { categoria };
    for (const serie of series) {
      ponto[serie.propertyId] = aplicar(
        agregacao,
        doGrupo.map(l => comoNumero(l[serie.propertyId]))
      );
    }
    return ponto;
  };

  const entradas = [...grupos.entries()];
  if (teto > 0 && entradas.length > teto) {
    const mantidas = entradas.slice(0, teto - 1);
    const resto = entradas.slice(teto - 1).flatMap(([, ls]) => ls);
    return [
      ...mantidas.map(([c, ls]) => pontoDe(c, ls)),
      pontoDe(CATEGORIA_OUTROS, resto),
    ];
  }

  return entradas.map(([c, ls]) => pontoDe(c, ls));
}

/**
 * A paleta — uma cor por NATUREZA, consistente entre os documentos (T4.3 do card).
 *
 * ⚠️ As cores são escolhidas para funcionar em tema claro E escuro, e para sobreviverem a
 * daltonismo de vermelho-verde: receita e despesa não se distinguem só pelo matiz (verde ×
 * vermelho), mas também pela **luminosidade**. Um par verde/vermelho de mesma luminosidade é
 * o erro mais comum de gráfico financeiro, e ele é invisível para quem o comete.
 */
export const PALETA: readonly string[] = [
  '#2563eb', // azul     — saldo / neutro
  '#10b981', // verde    — receita / entrada   ← claro
  '#b91c1c', // vermelho — despesa / saída     ← escuro
  '#d97706', // âmbar    — meta / orçado
  '#7c3aed', // roxo     — imposto / dedução
  '#0891b2', // ciano    — investimento
  '#be185d', // magenta
  '#4d7c0f', // oliva
] as const;

/*
 * ⚠️ O par verde/vermelho já foi `#059669`/`#dc2626`, e o teste o reprovou: contraste de
 * luminância **1,28**, ou seja, duas barras praticamente idênticas para quem tem daltonismo
 * de vermelho-verde — que é ~8% dos homens. Escolhidas por matiz, as duas cores pareciam
 * perfeitas; a diferença que importa não é a que quem escolhe consegue ver.
 *
 * O par atual tem **2,55**: o verde clareou e o vermelho escureceu, então eles se distinguem
 * em preto-e-branco. `test_grafico` afirma sobre a razão de luminância, não sobre os hexes —
 * trocar de cor é livre, desaparecer com o contraste não.
 */

export function corDaSerie(serie: Serie, indice: number): string {
  return serie.cor || PALETA[indice % PALETA.length];
}

/** O tipo de gráfico usa uma categoria por FATIA (pizza/rosca) em vez de por eixo? */
export function ehCircular(tipo: TipoDeGrafico): boolean {
  return tipo === 'pizza' || tipo === 'rosca';
}

/**
 * O que impede este gráfico de ser desenhado — vazio quando dá para desenhar.
 *
 * ⚠️ Devolve MOTIVO, não um booleano. Um bloco de gráfico em branco é a pior resposta
 * possível: quem o vê não sabe se faltou configurar, se o filtro zerou as linhas, ou se ele
 * está quebrado. Cada caso tem conserto diferente, e o bloco diz qual é.
 */
export function porqueNaoDaParaDesenhar(
  linhas: LinhaDeDados[],
  opcoes: Partial<OpcoesDeAgregacao> & { tipo?: TipoDeGrafico }
): string | null {
  if (!opcoes.eixoX) return 'Escolha a coluna do eixo X.';
  if (!opcoes.series?.length) return 'Escolha ao menos uma coluna de valores.';
  if (!linhas.length) {
    return 'A visão escolhida não tem nenhuma linha — confira o filtro da tabela.';
  }
  if (opcoes.tipo && ehCircular(opcoes.tipo) && opcoes.series.length > 1) {
    return 'Pizza e rosca desenham uma série só. Escolha uma coluna de valores.';
  }
  const pontos = agregar(linhas, opcoes as OpcoesDeAgregacao);
  const temAlgumValor = pontos.some(p =>
    (opcoes.series ?? []).some(s => p[s.propertyId] !== null)
  );
  if (!temAlgumValor) {
    return 'Nenhuma das linhas tem valor numérico nas colunas escolhidas.';
  }
  return null;
}

// ── as linhas da tabela, montadas ─────────────────────────────────────────────────────────

/** O mínimo de uma coluna para montar uma linha — o que `prop:columns` guarda. */
export interface ColunaDaTabela {
  id: string;
  type: string;
  name: string;
  data?: unknown;
}

/** Uma linha crua: o id do bloco filho e o texto dele (que é a coluna de título). */
export interface LinhaCrua {
  id: string;
  texto: string;
}

/**
 * Monta as linhas que o gráfico lê — **função pura**, e é por isso que ela mora aqui.
 *
 * ## As duas colunas que NÃO estão em `cells`
 *
 * `prop:cells` é `{rowId: {colId: {value}}}`, e a tentação é ler só isso. Duas colunas ficam
 * de fora, e cada uma custou um gráfico vazio no `dev`:
 *
 * | Coluna | Onde o valor está | O que acontecia sem tratar |
 * |---|---|---|
 * | **título** | no `text` do bloco filho (`block-utils.ts:106` especializa `columnId==='title'`) | eixo X com uma categoria só, vazia |
 * | **fórmula** | **em lugar nenhum** — é derivada, nasce da avaliação | *"Nenhuma das linhas tem valor numérico nas colunas escolhidas"* |
 *
 * O caminho da TABELA resolve as duas por `spacialProperties` do data-view. O gráfico lê o
 * **modelo cru** — não tem data-view — então precisa fazer o mesmo à mão.
 *
 * ⚠️ Reusar o avaliador só é possível porque ele é **puro** (decisão D-D: sem DOM, sem
 * BlockSuite, sem relógio). Se ele dependesse do data-view, a saída aqui seria reimplementá-lo
 * — e duas implementações da mesma fórmula divergem sem avisar.
 *
 * As árvores são analisadas **uma vez por chamada**, não por linha: com 412 lançamentos,
 * reanalisar por linha jogaria o parser fora 412 vezes.
 */
export function montarLinhas(
  colunas: ColunaDaTabela[],
  celulas: Record<string, Record<string, { value?: unknown }> | undefined>,
  linhasCruas: LinhaCrua[],
  ferramentas: {
    tipoDeFormula: string;
    analisar: (expressao: string, catalogo: CatalogoDeColunasMin) => unknown;
    avaliar: (arvore: unknown, ctx: ContextoMin) => unknown;
  }
): LinhaDeDados[] {
  const { tipoDeFormula, analisar, avaliar } = ferramentas;

  const catalogo: CatalogoDeColunasMin = {
    idPorNome: nome => colunas.find(c => c.name === nome)?.id,
    nomes: () => colunas.map(c => c.name),
  };

  const arvores = new Map<string, unknown>();
  for (const coluna of colunas) {
    if (coluna.type !== tipoDeFormula) continue;
    const expressao = (coluna.data as { expressao?: string } | undefined)?.expressao;
    if (!expressao) { arvores.set(coluna.id, null); continue; }
    try {
      arvores.set(coluna.id, analisar(expressao, catalogo));
    } catch {
      // Fórmula quebrada não derruba o gráfico: a coluna fica sem valor, e o
      // `porqueNaoDaParaDesenhar` dirá que não há número — que é a verdade.
      arvores.set(coluna.id, null);
    }
  }

  return linhasCruas.map(crua => {
    const linha: LinhaDeDados = {};
    const daLinha = celulas[crua.id];

    /*
     * ⚠️ **O título entra SEMPRE, mesmo quando a tabela não o declara.**
     *
     * `prop:columns` de uma tabela criada pelo comando do upstream **não tem** entrada de
     * título — a coluna `Title` aparece na tela e não existe no modelo. Só as tabelas que o
     * nosso semeador monta declaram `{id:'title', type:'title'}` (é o 3º dos quatro defeitos
     * de forma da Regra 8.49).
     *
     * Enquanto o laço abaixo era o único caminho, um gráfico com `eixoX: 'title'` sobre uma
     * tabela feita à mão via `/` recebia linhas SEM título: as três categorias viravam uma só,
     * e o gráfico desenhava **uma barra gorda** somando tudo — sem erro, sem aviso, e com
     * aparência de gráfico. Medido no `dev` em 2026-08-28, e é o defeito que o campo pegou
     * depois de 24 testes verdes.
     *
     * O texto da linha está aqui o tempo todo (`crua.texto`); faltava alguém entregá-lo. O
     * laço abaixo ainda pode SOBRESCREVER isto — se a tabela declara a coluna de título, quem
     * manda é a declaração dela.
     */
    linha[COLUNA_DE_TITULO] = crua.texto;

    for (const coluna of colunas) {
      if (coluna.type === 'title') { linha[coluna.id] = crua.texto; continue; }
      if (coluna.type === tipoDeFormula) continue; // 2ª passada
      linha[coluna.id] = daLinha?.[coluna.id]?.value;
    }

    // ⚠️ 2ª passada. `valorDaCelula` resolve fórmula-lendo-fórmula por recursão, e quem impede
    // que isso vire recursão infinita é o `detectarCiclo` de dentro do próprio avaliador.
    const valorDaCelula = (id: string): unknown => {
      if (id in linha) return linha[id];
      const arvore = arvores.get(id);
      if (!arvore) return undefined;
      try {
        const v = avaliar(arvore, {
          valorDaCelula,
          formulaDaColuna: outro => arvores.get(outro) ?? null,
          propertyIdAtual: id,
        });
        linha[id] = v;
        return v;
      } catch {
        return undefined;
      }
    };
    for (const [id] of arvores) valorDaCelula(id);

    return linha;
  });
}

/** O mínimo do catálogo que o parser exige — evita importar o tipo e criar acoplamento. */
export interface CatalogoDeColunasMin {
  idPorNome(nome: string): string | undefined;
  nomes(): string[];
}

/** O mínimo do contexto que o avaliador exige. */
export interface ContextoMin {
  valorDaCelula(propertyId: string): unknown;
  formulaDaColuna(propertyId: string): unknown;
  propertyIdAtual: string;
}

// ── a configuração que o gráfico ganha ao NASCER ──────────────────────────────────────────

/**
 * Os tipos de coluna que rendem número — e portanto podem ser uma série.
 *
 * `number` é o do upstream; `tuninho-formula` é a nossa coluna de fórmula, que existe
 * justamente para somar (card 1850168865112196130). Um `select` ou um `rich-text` não entram:
 * o gráfico os leria como `null` e desenharia uma linha reta no zero, que é pior que recusar.
 */
export const TIPOS_NUMERICOS = Object.freeze(['number', 'tuninho-formula']);

/** O id da coluna de título, que o BlockSuite grava LITERAL — nunca um id gerado. */
export const COLUNA_DE_TITULO = 'title';

/**
 * Escolhe eixo X e série para um gráfico recém-inserido.
 *
 * ## Por que isto existe, e por que é uma função pura
 *
 * Inserir um gráfico **sem** eixo e **sem** série produz um bloco que diz *"Escolha a coluna
 * do eixo X"* — e até este card não havia onde escolher. Um item de menu que entrega isso é
 * a Regra 8.41 do `CLAUDE.md` na letra: uma ação que PARECE resolver e devolve o mesmo
 * sintoma. Então o gráfico nasce configurado, e o seletor (que este card também traz) fica
 * para quem quiser trocar — não para quem precisa consertar.
 *
 * Pura porque a escolha é a parte que se erra, e é a que se testa nesta estação sem
 * navegador e sem os 16 minutos de build do Affine (Regra 8.7.3) — o mesmo motivo pelo qual
 * `montarLinhas` mora aqui.
 *
 * ⚠️ **O eixo X é `'title'` e não a primeira coluna.** A coluna de título não vive em
 * `prop:columns` como as outras — ela é o texto do bloco-filho, e o id dela é o literal
 * `'title'`. Escolher `colunas[0]` pegaria a primeira coluna COMUM e deixaria o gráfico
 * rotulado por um campo qualquer. Foi um dos quatro defeitos que só o navegador revelou no
 * card do financeiro (Regra 8.49).
 */
export function configuracaoInicial(
  colunas: readonly { id: string; type: string; name: string }[]
): { eixoX: string; series: Serie[] } {
  const numerica = colunas.find(
    c => TIPOS_NUMERICOS.includes(c.type) && c.id !== COLUNA_DE_TITULO
  );
  return {
    eixoX: COLUNA_DE_TITULO,
    series: numerica
      ? [{ propertyId: numerica.id, rotulo: numerica.name || 'Valor' }]
      : [],
  };
}

// ── a tabela que alimenta um gráfico ──────────────────────────────────────────────────────

/**
 * O mínimo de um bloco para esta busca.
 *
 * ⚠️ Estrutural, e NÃO `DatabaseBlockModel`. É o que mantém este arquivo puro — e puro é o
 * que faz `cli/tests/js/insercao-do-grafico.test.ts` rodar nesta estação, sem o
 * `node_modules` do Affine e sem os ~16 minutos de build da Regra 8.7.3. A primeira versão
 * desta função morava em `configs/slash-menu.ts`, ao lado do item de menu — e o teste morreu
 * com `ERR_MODULE_NOT_FOUND` na primeira execução, porque aquele arquivo importa
 * `@blocksuite/...`. O gancho `cli/tests/js/resolve-ts.mjs` avisa disso no cabeçalho: ele
 * NÃO é um bundler e não deve virar um.
 */
export interface BlocoMin {
  id: string;
  flavour: string;
  children?: readonly BlocoMin[];
}

/**
 * A tabela que este gráfico vai ler.
 *
 * Procura a `affine:database` mais próxima **acima** do cursor e, não achando, a primeira do
 * documento. A ordem importa: num documento com várias tabelas (o `📈 DRE` tem duas), quem
 * digita `/` logo abaixo de uma delas está falando daquela.
 *
 * ⚠️ Devolve `null` quando não há nenhuma — e quem chama **cria** uma. Não é o mesmo que
 * falhar: é a diferença entre um gráfico que nasce ligado e um que nasce pedindo o que
 * ninguém pode dar.
 */
export function acharTabela<B extends BlocoMin>(
  store: {
    getBlocksByFlavour(f: string): { model: B }[];
    getParent(m: B): BlocoMin | null;
  },
  ancora: B | undefined
): B | null {
  const todas = store
    .getBlocksByFlavour('affine:database')
    .map(b => b.model);
  if (todas.length === 0) return null;

  // A "mais próxima acima" é a última tabela IRMÃ que vem antes do cursor. Procurar entre os
  // irmãos, e não na lista global, é o que faz a resposta certa num documento com duas notas:
  // `getBlocksByFlavour` devolve as duas notas misturadas em ordem de documento, e a tabela
  // "anterior" poderia estar noutra nota, longe do que a pessoa está olhando.
  if (ancora) {
    const pai = store.getParent(ancora);
    if (pai) {
      const irmaos = pai.children;
      const ondeEstou = irmaos.findIndex(f => f.id === ancora.id);
      if (ondeEstou >= 0) {
        for (let i = ondeEstou; i >= 0; i--) {
          const irmao = irmaos[i];
          if (irmao && irmao.flavour === 'affine:database') {
            return irmao as B;
          }
        }
      }
    }
  }

  // Sem tabela acima do cursor, a primeira do documento. Documento com uma tabela só — o caso
  // comum — cai aqui e acerta.
  return todas[0] ?? null;
}

